import { useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import type {
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js';
import {
  Elements,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { CheckoutErrorDescriptor } from './errorPresentation';
import {
  CheckoutFlowError,
  presentLocalizedError,
  presentProviderError,
  presentSystemError,
} from './errorPresentation';
import { stripeLocale, tr, useCurrentLocale } from '../i18n';

export type WalletPaymentMethod = 'APPLE_PAY' | 'GOOGLE_PAY';
export type BuyerWalletPaymentMethod = 'apple_pay' | 'google_pay';

export interface WalletConfirmPayload {
  paymentMethod: WalletPaymentMethod;
  stripeConfirmationTokenId: string;
  confirmAction(clientSecret: string): Promise<{ ok: true } | { error: CheckoutErrorDescriptor }>;
}

type WalletBandState = 'probing' | 'visible' | 'hidden';

interface ExpressWalletBandProps {
  amountInMinorUnits?: number;
  amountInCents?: number;
  currency: string;
  publishableKey: string;
  onBehalfOf?: string;
  walletMethods: BuyerWalletPaymentMethod[];
  deliveryEmail?: string;
  disabled?: boolean;
  onConfirmWallet: (payload: WalletConfirmPayload) => Promise<void>;
  onBeforeWalletOpen?: (paymentMethod: WalletPaymentMethod) => boolean;
  onAvailabilityChange?: (available: boolean) => void;
  onWalletActivityChange?: (paymentMethod: WalletPaymentMethod | null) => void;
  onWalletError?: (error: CheckoutErrorDescriptor) => void;
}

export function ExpressWalletBand({
  amountInMinorUnits,
  amountInCents,
  currency,
  publishableKey,
  onBehalfOf,
  walletMethods,
  deliveryEmail,
  disabled,
  onConfirmWallet,
  onBeforeWalletOpen,
  onAvailabilityChange,
  onWalletActivityChange,
  onWalletError,
}: ExpressWalletBandProps) {
  const locale = useCurrentLocale();
  const resolvedAmountInMinorUnits = amountInMinorUnits ?? amountInCents ?? 0;
  const stripePromise = useMemo(() => (publishableKey.length > 0 ? loadStripe(publishableKey) : null), [publishableKey]);
  const elementsOptions = useMemo(
    () => ({
      mode: 'payment' as const,
      amount: resolvedAmountInMinorUnits,
      currency: currency.toLowerCase(),
      paymentMethodTypes: ['card'],
      setupFutureUsage: 'off_session' as const,
      ...(onBehalfOf !== undefined ? { onBehalfOf } : {}),
      locale: stripeLocale(),
    }),
    [resolvedAmountInMinorUnits, currency, locale, onBehalfOf],
  );

  if (resolvedAmountInMinorUnits <= 0 || publishableKey.length === 0 || walletMethods.length === 0) {
    return null;
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions} key={`${publishableKey}:${onBehalfOf ?? ''}`}>
      <ExpressWalletBandInner
        walletMethods={walletMethods}
        {...(deliveryEmail !== undefined ? { deliveryEmail } : {})}
        {...(disabled !== undefined ? { disabled } : {})}
        onConfirmWallet={onConfirmWallet}
        {...(onBeforeWalletOpen !== undefined ? { onBeforeWalletOpen } : {})}
        {...(onAvailabilityChange !== undefined ? { onAvailabilityChange } : {})}
        {...(onWalletActivityChange !== undefined ? { onWalletActivityChange } : {})}
        {...(onWalletError !== undefined ? { onWalletError } : {})}
      />
    </Elements>
  );
}

function ExpressWalletBandInner({
  walletMethods,
  deliveryEmail,
  disabled,
  onConfirmWallet,
  onBeforeWalletOpen,
  onAvailabilityChange,
  onWalletActivityChange,
  onWalletError,
}: Pick<
  ExpressWalletBandProps,
  | 'walletMethods'
  | 'deliveryEmail'
  | 'disabled'
  | 'onConfirmWallet'
  | 'onBeforeWalletOpen'
  | 'onAvailabilityChange'
  | 'onWalletActivityChange'
  | 'onWalletError'
>) {
  const stripe = useStripe();
  const elements = useElements();
  const [bandState, setBandState] = useState<WalletBandState>('probing');
  const availabilityCallbackRef = useRef(onAvailabilityChange);
  const receiptEmailMarker = '__VP_RECEIPT_EMAIL__';
  const [receiptPrefix = '', receiptSuffix = ''] = tr('payment.walletReceipt', {
    email: receiptEmailMarker,
  }).split(receiptEmailMarker);

  useEffect(() => {
    availabilityCallbackRef.current = onAvailabilityChange;
  }, [onAvailabilityChange]);

  useEffect(() => {
    if (bandState !== 'probing') {
      return undefined;
    }
    const timeout = window.setTimeout(() => {
      setBandState('hidden');
      availabilityCallbackRef.current?.(false);
    }, 1800);
    return () => window.clearTimeout(timeout);
  }, [bandState]);

  const allowsApplePay = walletMethods.includes('apple_pay');
  const allowsGooglePay = walletMethods.includes('google_pay');

  const options = useMemo<StripeExpressCheckoutElementOptions>(
    () => ({
      buttonHeight: 48,
      layout: {
        maxColumns: 2,
        overflow: 'never',
      },
      emailRequired: false,
      paymentMethods: {
        applePay: allowsApplePay ? 'auto' : 'never',
        googlePay: allowsGooglePay ? 'auto' : 'never',
        amazonPay: 'never',
        link: 'never',
        paypal: 'never',
        klarna: 'never',
      },
      buttonType: {
        applePay: 'buy',
        googlePay: 'buy',
      },
      buttonTheme: {
        applePay: 'black',
        googlePay: 'black',
      },
    }),
    [allowsApplePay, allowsGooglePay],
  );

  function setAvailability(available: boolean) {
    setBandState(available ? 'visible' : 'hidden');
    availabilityCallbackRef.current?.(available);
  }

  function handleReady(event: StripeExpressCheckoutElementReadyEvent) {
    const methods = event.availablePaymentMethods;
    setAvailability(Boolean((allowsApplePay && methods?.applePay) || (allowsGooglePay && methods?.googlePay)));
  }

  function handleAvailablePaymentMethods(event: StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent) {
    const methods = event.paymentMethods;
    setAvailability(Boolean(
      (allowsApplePay && methods?.applePay?.available) || (allowsGooglePay && methods?.googlePay?.available),
    ));
  }

  function handleClick(event: StripeExpressCheckoutElementClickEvent) {
    if (disabled) {
      event.reject();
      return;
    }
    const paymentMethod = walletMethodFromStripe(event.expressPaymentType);
    if (paymentMethod === null) {
      event.reject();
      return;
    }
    if (onBeforeWalletOpen?.(paymentMethod) === false) {
      event.reject();
      return;
    }
    onWalletActivityChange?.(paymentMethod);
    event.resolve();
  }

  function handleCancel() {
    onWalletActivityChange?.(null);
  }

  async function handleConfirm(event: StripeExpressCheckoutElementConfirmEvent) {
    const paymentMethod = walletMethodFromStripe(event.expressPaymentType);
    if (paymentMethod === null || stripe === null || elements === null) {
      event.paymentFailed({ reason: 'fail' });
      reportWalletError(presentLocalizedError({ key: 'error.walletStart', kind: 'payment' }));
      return;
    }
    onWalletActivityChange?.(paymentMethod);
    const submit = await elements.submit();
    if (submit.error) {
      event.paymentFailed({ reason: 'fail' });
      reportWalletError(
        presentProviderError({
          ...(submit.error.code !== undefined ? { code: submit.error.code } : {}),
          ...(submit.error.decline_code !== undefined ? { declineCode: submit.error.decline_code } : {}),
          ...(submit.error.message !== undefined ? { message: submit.error.message } : {}),
          fallbackKey: 'error.walletProvider',
        }),
      );
      return;
    }
    const token = await stripe.createConfirmationToken({ elements });
    if (token.error || token.confirmationToken === undefined) {
      event.paymentFailed({ reason: 'fail' });
      reportWalletError(
        presentProviderError({
          ...(token.error?.code !== undefined ? { code: token.error.code } : {}),
          ...(token.error?.decline_code !== undefined ? { declineCode: token.error.decline_code } : {}),
          ...(token.error?.message !== undefined ? { message: token.error.message } : {}),
          fallbackKey: 'error.walletProvider',
        }),
      );
      return;
    }
    try {
      await onConfirmWallet({
        paymentMethod,
        stripeConfirmationTokenId: token.confirmationToken.id,
        confirmAction: async (clientSecret: string) => {
          const result = await stripe.handleNextAction({ clientSecret });
          if (result.error) {
            return {
              error: presentProviderError({
                ...(result.error.code !== undefined ? { code: result.error.code } : {}),
                ...(result.error.decline_code !== undefined ? { declineCode: result.error.decline_code } : {}),
                ...(result.error.message !== undefined ? { message: result.error.message } : {}),
                fallbackKey: 'error.walletAuthentication',
              }),
            };
          }
          return { ok: true };
        },
      });
    } catch (error) {
      event.paymentFailed({ reason: 'fail' });
      reportWalletError(
        error instanceof CheckoutFlowError
          ? error.descriptor
          : error instanceof Error
            ? presentProviderError({ message: error.message, fallbackKey: 'error.walletProvider' })
            : presentSystemError(undefined, 'retry'),
      );
    }
  }

  function reportWalletError(error: CheckoutErrorDescriptor) {
    onWalletError?.(error);
    onWalletActivityChange?.(null);
  }

  return (
    <div className={`vp-wallet-band is-${bandState}`}>
      <p className="vp-wallet-email-note">
        {receiptPrefix}
        {deliveryEmail === undefined ? tr('common.emailFallback') : <strong>{deliveryEmail}</strong>}
        {receiptSuffix}
      </p>
      <ExpressCheckoutElement
        options={options}
        onReady={handleReady}
        onAvailablePaymentMethodsChange={handleAvailablePaymentMethods}
        onClick={handleClick}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </div>
  );
}

function walletMethodFromStripe(value: string): WalletPaymentMethod | null {
  if (value === 'apple_pay') {
    return 'APPLE_PAY';
  }
  if (value === 'google_pay') {
    return 'GOOGLE_PAY';
  }
  return null;
}
